import { useState, FocusEvent, useRef, useEffect } from 'react';
import RoomCompositionComponent from '../room/room-composition.component';
import PopOverComponent, {
	PopOverAlignment
} from '../pop-over/pop-over.component';
import { TextFieldComponent } from '@vaa-component-lib/component.atom.text-field';
import CriteriaTriggerComponent from '../criteria-trigger/criteria-trigger.component';
import { RoomField, RoomInput } from './room-field.styles';
import sanitiseString from '../../lib/utils/sanitise-string';
import { RoomComposition, Rooms } from '../../lib/utils/composition.service';
import { DEFAULT_ROOMS } from '../../lib/utils/constants';
import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';
import {
	IconNavigationChevronUpComponent,
	IconNavigationChevronDownComponent,
	IconActionUserComponent,
	IconActionUserGroupComponent,
	IconActionUserLargeGroupComponent,
	IconsSize
} from '@vaa-component-lib/component.atom.icons';
import {
	TypographyComponent,
	TypographySize,
	TypographyVariant
} from '@vaa-component-lib/component.atom.typography';

export interface RoomFieldComponentProps {
	label: string | undefined;
	name: string;
	placeholder?: string;
	id: string | undefined;
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
	value: RoomComposition[] | undefined;
	hasError?: boolean;
	feedback?: string;
	roomsDisabled?: boolean;
}

const RoomFieldComponent = ({
	label,
	placeholder,
	id,
	onBlur,
	value,
	hasError,
	feedback,
	name,
	roomsDisabled = false
}: RoomFieldComponentProps) => {
	const [showPopOver, setShowPopOver] = useState<boolean>(false);
	const [prettyValue, setPrettyValue] = useState<string>(
		Rooms.prettyFormat(value || DEFAULT_ROOMS, roomsDisabled)
	);
	const [totalPassengers, setTotalPassengers] = useState<number>(
		Rooms.totalPassengers(value || DEFAULT_ROOMS)
	);

	const handleFocus = () => {
		setShowPopOver(true);
	};

	const toggleFocus = () => {
		setShowPopOver(!showPopOver);
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		if (
			e.relatedTarget &&
			ref.current &&
			!ref.current.contains(e.relatedTarget)
		) {
			setShowPopOver(false);
		}
		if (onBlur) {
			onBlur(e);
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (ref.current && !ref.current.contains(event.target as Node)) {
			setShowPopOver(false);
		}
	};

	const popOverId = `popover-${sanitiseString(label || '')}`;

	const ref = useRef<HTMLDivElement>(null);

	const isMobile = useMediaQuery(`(max-width: ${BreakPointToken.SmMax}px)`);

	const getPassengerIcon = (passengerCount: number) => {
		switch (passengerCount) {
			case 1:
				return <IconActionUserComponent size={IconsSize.Sml} />;
			case 2:
				return <IconActionUserGroupComponent size={IconsSize.Sml} />;
			default:
				return <IconActionUserLargeGroupComponent size={IconsSize.Sml} />;
		}
	};

	const popOverFooterContent = (
		<TypographyComponent
			element="p"
			size={TypographySize.Sml}
			variant={TypographyVariant.Body}>
			{prettyValue}
		</TypographyComponent>
	);

	useEffect(() => {
		if (document) {
			if (showPopOver) {
				document.addEventListener('mousedown', handleClickOutside);
			}

			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [showPopOver]);

	useEffect(() => {
		if (value) {
			setPrettyValue(Rooms.prettyFormat(value, roomsDisabled));
			setTotalPassengers(Rooms.totalPassengers(value));
		}
	}, [value]);

	return (
		<RoomField data-cy="room-field" ref={ref}>
			{isMobile && (
				<CriteriaTriggerComponent
					{...{ label }}
					value={prettyValue}
					onClick={() => setShowPopOver(true)}
					errorMessage={feedback}
					hasError={hasError}
					aria={{ controls: popOverId, expanded: showPopOver }}
					iconChild={getPassengerIcon(totalPassengers)}
				/>
			)}

			<RoomInput hidden={isMobile}>
				<TextFieldComponent
					{...{
						label,
						placeholder,
						id
					}}
					value={prettyValue}
					autoComplete="off"
					spellCheck={false}
					placeholder={placeholder}
					onFocus={handleFocus}
					onBlur={handleBlur}
					aria-controls={popOverId}
					aria-haspopup="true"
					feedback={showPopOver ? '' : feedback}
					hasError={hasError}
					iconChild={
						showPopOver ? (
							<IconNavigationChevronUpComponent
								aria-label="Close modal"
								// @ts-ignore
								role="img"
							/>
						) : (
							<IconNavigationChevronDownComponent
								aria-label="Open modal"
								// @ts-ignore
								role="img"
							/>
						)
					}
					onIconClick={toggleFocus}
					readonly
				/>
			</RoomInput>

			<PopOverComponent
				id={popOverId}
				isVisible={showPopOver}
				title="Who's going?"
				footerBtnText="Next"
				footerContent={isMobile && popOverFooterContent}
				footerOnClick={(): void => setShowPopOver(false)}
				onClose={(): void => setShowPopOver(false)}
				alignment={PopOverAlignment.Right}
				variableHeight={true}
				isOverflow={true}>
				<RoomCompositionComponent
					name={name}
					value={value ? value : DEFAULT_ROOMS}
					roomsDisabled={roomsDisabled}
				/>
			</PopOverComponent>
		</RoomField>
	);
};

export default RoomFieldComponent;
